import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { formatMoney } from '../../utils/configUtils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

/* ─── Rounded bar shape ───────────────────────────────────────────── */
const RoundedBar = (props) => {
  const { x, y, width, height, fill } = props;
  if (!height || height <= 0) return null;
  const r = Math.min(8, width / 2, height / 2);
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} rx={r} ry={r} />
    </g>
  );
};

/* ─── Tooltip ─────────────────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const isRevenue = entry?.dataKey === 'totalRevenue';
  const value = entry?.value ?? 0;
  const color = isRevenue ? '#065F46' : '#6B5344';

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #f0f0f0',
      borderRadius: 14,
      padding: '10px 14px',
      boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
      fontFamily: 'Heebo, sans-serif',
      minWidth: 140,
    }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color }}>
        {isRevenue ? formatMoney(value) : value.toLocaleString() + ' sold'}
      </div>
    </div>
  );
};

/* ─── Chart inner ─────────────────────────────────────────────────── */
const CustomGraph = ({ data, dataKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRevenue = dataKey === 'totalRevenue';

  /* Unique gradient IDs — avoids conflicts when both charts share the SVG namespace */
  const gradId = isRevenue ? 'cwg_grad_revenue' : 'cwg_grad_sales';
  const colorTop = isRevenue ? '#065F46' : '#6B5344';
  const colorBot = isRevenue ? '#0D9488' : '#9b7a6a';

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 200 : 270}>
      <BarChart
        data={data}
        margin={{ top: 10, right: 8, bottom: 0, left: isMobile ? -8 : 0 }}
        barCategoryGap="38%"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colorTop} stopOpacity={0.92} />
            <stop offset="100%" stopColor={colorBot} stopOpacity={0.3} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f5f5f5" />

        <XAxis
          dataKey="name"
          tick={{ fontSize: isMobile ? 10 : 12, fill: '#9ca3af', fontFamily: 'Heebo' }}
          axisLine={false}
          tickLine={false}
          dy={6}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: isMobile ? 10 : 11, fill: '#9ca3af', fontFamily: 'Heebo' }}
          width={isRevenue ? (isMobile ? 44 : 58) : (isMobile ? 26 : 34)}
          tickFormatter={(v) => {
            if (!isRevenue) return v;
            const d = v / 100;
            if (d >= 1000) return '$' + (d / 1000).toFixed(1) + 'k';
            return '$' + d.toFixed(0);
          }}
        />

        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(6,95,70,0.04)', radius: [6, 6, 0, 0] }} />

        <Bar
          dataKey={dataKey}
          fill={`url(#${gradId})`}
          shape={<RoundedBar />}
          maxBarSize={36}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ─── Card wrapper ────────────────────────────────────────────────── */
const CustomCardWithGraph = ({ title, data, dataKey, customStyles }) => {
  const isRevenue = dataKey === 'totalRevenue';
  const accentColor = isRevenue ? '#065F46' : '#6B5344';

  return (
    <Box
      sx={{
        borderRadius: '24px',
        background: '#fff',
        boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)',
        p: { xs: 2, sm: '20px' },
        overflow: 'hidden',
        ...customStyles,
      }}
    >
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
          <Box sx={{ width: 4, height: 20, borderRadius: '2px', bgcolor: accentColor, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 16 }, color: '#1a1a1a', fontFamily: 'Heebo' }}>
            {title}
          </Typography>
        </Box>
      )}
      <CustomGraph data={data} dataKey={dataKey} />
    </Box>
  );
};

export default CustomCardWithGraph;
